/**
 * UranIA PA — OIDC redirect callback (US-010 AC#4).
 *
 * Route `/auth/callback` — Keycloak vi reindirizza con `?code=...&state=...`
 * dopo il consenso. Effetti:
 *   1. `completeFederatedLogin()` chiama `signinRedirectCallback()` (valida
 *      state+nonce PKCE), popola `useAuthStore` con principal+access_token.
 *   2. Naviga al `returnTo` (default `/dashboard`, AC#4) tramite TanStack
 *      Router.
 *
 * In caso di errore (state mismatch, JWT invalido, ecc.) renderizza un
 * messaggio in italiano + bottone "Torna alla pagina di accesso".
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link, useRouter } from '@tanstack/react-router';
import type { JSX } from 'react';

import { completeFederatedLogin } from '../lib/auth/login-actions.ts';
import { Route as RootRoute } from './__root.tsx';

type State = { kind: 'processing' } | { kind: 'error'; message: string };

// Module-level singleton: l'authorization code OIDC e' single-use. Senza
// questo, React.StrictMode in dev esegue useEffect 2 volte e la SPA chiama
// `signinRedirectCallback()` 2 volte sullo stesso code -> il secondo scambio
// fallisce con `invalid_code` ("Code already used" lato Keycloak), e l'utente
// vede "Accesso non riuscito" anche dopo un login a Keycloak riuscito.
let callbackPromise: Promise<string> | null = null;

function getOrStartCallback(): Promise<string> {
  if (callbackPromise === null) {
    callbackPromise = completeFederatedLogin();
  }
  return callbackPromise;
}

function CallbackPage(): JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();
  const [state, setState] = useState<State>({ kind: 'processing' });

  useEffect(() => {
    let cancelled = false;
    void getOrStartCallback()
      .then((returnTo) => {
        if (cancelled) {
          return;
        }
        void router.navigate({ to: returnTo });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof Error && err.message.length > 0 ? err.message : t('login.callback_failed');
        setState({ kind: 'error', message });
        // Reset singleton on error: un eventuale retry manuale (nuovo /login)
        // parte da una promise pulita.
        callbackPromise = null;
      });
    return (): void => {
      cancelled = true;
    };
  }, [router, t]);

  if (state.kind === 'processing') {
    return (
      <section data-testid="auth-callback-processing" aria-live="polite">
        <p className="lead">{t('login.callback_processing')}</p>
      </section>
    );
  }
  return (
    <section data-testid="auth-callback-error" aria-live="assertive">
      <div className="alert alert-danger" role="alert">
        <p className="mb-2">{t('login.callback_failed')}</p>
        <p className="small text-muted mb-0">{state.message}</p>
      </div>
      <Link to="/accedi" className="btn btn-primary">
        {t('login.callback_retry')}
      </Link>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/auth/callback',
  component: CallbackPage,
});
